"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ApiError, getAssetProfile, stockTokenName, symbolLabel, type AssetProfile } from "@/lib/api";
import { COIN_LOGOS } from "@/lib/coin-logos";

/** Instrument marks, drawn INLINE rather than fetched.
 *
 *  No CDN and no image files: the console ships one bundle and these render offline, survive a
 *  strict CSP, and cannot 404 the way a remote coin-icon service can.
 *
 *  REDRAWN 2026-09-25. The first set was "geometry, not artwork" and legible only at 14px — the ₿ was two stems and two bowls that read as a glyph in
 *  a chip. These are the marks a trader already knows, on a 64-unit grid so they stay crisp from
 *  14px in a chip to 40px on the landing page: the Bitcoin B at its 14° tilt, Ethereum's shaded
 *  octahedron, Solana's three bars, XRP's crossed arcs. Brand hues are hard-coded on purpose —
 *  that is how the instrument is recognised at a glance, on either theme.
 *
 *  GOLD and OIL are OUR labels for contracts (XAUUSDT / CLUSDT), not token brands, so they take
 *  the commodity's own look — a stacked gold bar on a warm disc, a crude drop on a dark one — and
 *  never read as a coin logo.
 *
 *  A coin without a drawn mark gets a disc carrying its ticker (never a borrowed logo): new symbols
 *  appear whenever a master's or Spot's pair list changes, and an empty space read as broken. */

/** TOKENIZED SHARES on Spot (2026-10-10): bybit's xStocks end in X (AAPLXUSDT), binance's tokens
 *  in B (AAPLBUSDT, SPYBUSDT). Each share: its company name and brand colour, shown as a disc
 *  carrying the ticker — a company logo is a trademark we do not redraw. */
const STOCKS: Record<string, { name: string; bg: string }> = {
  AAPL: { name: "Apple", bg: "#1d1d1f" },
  AMZN: { name: "Amazon", bg: "#ff9900" },
  GOOGL: { name: "Alphabet (Google)", bg: "#4285f4" },
  META: { name: "Meta", bg: "#0866ff" },
  MSFT: { name: "Microsoft", bg: "#00a4ef" },
  NVDA: { name: "Nvidia", bg: "#76b900" },
  TSLA: { name: "Tesla", bg: "#cc0000" },
  COIN: { name: "Coinbase", bg: "#0052ff" },
  MSTR: { name: "Strategy", bg: "#f26722" },
  HOOD: { name: "Robinhood", bg: "#00c805" },
  CRCL: { name: "Circle", bg: "#2775ca" },
};
/** COINS WITHOUT A DRAWN MARK YET (2026-10-10, the Spot list): a disc in the coin's colour with its
 *  ticker, so a tile never shows an empty space where the mark goes. */
const LETTER_COINS: Record<string, { name: string; bg: string; fg?: string }> = {
  DOGE: { name: "Dogecoin", bg: "#c2a633" },
  SHIB: { name: "Shiba Inu", bg: "#e42d04" },
  PEPE: { name: "Pepe", bg: "#3d9a3d" },
  BONK: { name: "Bonk", bg: "#f8a51b", fg: "#1a1a1a" },
  WIF: { name: "dogwifhat", bg: "#b58b6a" },
};


/** The share behind a stock token's base (AAPLX / AAPLB -> AAPL, SPYB -> SPY, MEXC's Ondo TSLAON ->
 *  TSLA), else null. */
function shareOf(base: string): string | null {
  const t = /ON$/.test(base) && base.length > 3 ? base.slice(0, -2) : /[XB]$/.test(base) ? base.slice(0, -1) : null;
  if (t === null) return null;
  return t in STOCKS || t === "SPY" || t === "QQQ" ? t : null;
}

/** Plain-language name for the markets strip and any tooltip. `symbolLabel` stays the short one. */
export function symbolName(symbol: string): string {
  const base = baseOf(symbol);
  const share = shareOf(base);
  if (share === "SPY") return "S&P 500 (SPY)";
  if (share === "QQQ") return "Nasdaq 100 (QQQ)";
  if (share) return STOCKS[share]!.name;
  const listed = stockTokenName(symbol);
  if (listed) return listed;
  switch (base) {
    case "BTC":
      return "Bitcoin";
    case "ETH":
      return "Ethereum";
    case "SOL":
      return "Solana";
    case "XRP":
      return "XRP";
    case "BNB":
      return "BNB";
    case "NEAR":
      return "NEAR Protocol";
    case "POL":
      return "Polygon";
    case "USDC":
      return "USD Coin";
    case "USDT":
      return "Tether";
    case "DOGE":
    case "SHIB":
    case "PEPE":
    case "BONK":
    case "WIF":
      return LETTER_COINS[base]!.name;
    case "XAU":
    case "XAUUSD": // cTrader's gold
    case "XAUT":
    case "PAXG":
    case "GOLD":
      return "Gold";
    case "XAG":
    case "XAGUSD": // cTrader's silver, when a broker name reaches the icon
    case "SILVER":
      return "Silver";
    case "QQQ":
    case "US TECH 100": // cTrader's Nasdaq 100 CFD
      return "Nasdaq 100";
    case "SPY":
    case "US 500": // cTrader's S&P 500 CFD
      return "S&P 500";
    case "US30":
    case "US 30": // the broker's own name for the Dow CFD
      return "Dow 30";
    case "CL":
    case "OIL":
    case "WTI":
    case "XTIUSD": // cTrader's WTI (a follower copy of CLUSDT)
      return "Crude oil";
    case "EURUSD":
      return "Euro / US dollar";
    case "AUDUSD":
      return "Australian dollar / US dollar";
    case "GBPUSD":
      return "British pound / US dollar";
    case "USDJPY":
      return "US dollar / Japanese yen";
    default:
      return symbolLabel(symbol);
  }
}

/** FOREX (owner 2026-10-01: "add forex logo too like we did for the other symbols"), cTrader only.
 *  A pair is two currencies, so the mark is two overlapping discs — the base on top, the quote
 *  behind — each carrying its currency sign in its own colour: EU blue, Australian navy, dollar
 *  green, Japanese red, British red. Signs, not flags: a flag is unreadable at 14px, a € is not. */
const CURRENCY: Record<string, { sign: string; bg: string; fg: string; size: number }> = {
  EUR: { sign: "€", bg: "#003399", fg: "#ffcc00", size: 24 },
  AUD: { sign: "A$", bg: "#012169", fg: "#ffffff", size: 17 },
  USD: { sign: "$", bg: "#1f7a3f", fg: "#ffffff", size: 24 },
  JPY: { sign: "¥", bg: "#ffffff", fg: "#bc002d", size: 24 },
  GBP: { sign: "£", bg: "#c8102e", fg: "#ffffff", size: 24 },
};
const FOREX_PAIRS = new Set(["EURUSD", "AUDUSD", "USDJPY", "GBPUSD"]);

/** THE HOME PAGE'S ONE FOREX TILE (owner 2026-10-02: "give every forex their own symbol, it's only
 *  on home page we keep the blue one"). `SymbolIcon symbol="FOREX"` draws a single disc reading "FX";
 *  every real pair keeps its own two-currency mark above. */
export const FOREX_TILE = "FOREX";

function baseOf(symbol: string): string {
  return (symbol ?? "").toUpperCase().replace(/USDT$|USDC$|PERP$/, "");
}

/** A coin's logo file, or null: the perpetuals' 1000PEPE / 1000000MOG / 10000SATS are the coin. */
function logoOf(base: string): string | null {
  const coin = base.replace(/^1(0{3,})/, "");
  const ext = COIN_LOGOS[coin];
  return ext ? `/logos/${coin}.${ext}` : null;
}

/** THE COIN'S OWN LOGO (2026-10-28), a round image from the console's own files (`COIN_LOGOS`). A
 *  file that fails to load shows the ticker disc instead, so a chip is never empty. */
function CoinLogo({ src, size, className, fallback }: {
  src: string; size: number; className: string; fallback: ReactNode;
}) {
  const [broken, setBroken] = useState(false);
  if (broken) return <>{fallback}</>;
  return (
    // eslint-disable-next-line @next/next/no-img-element -- a 14–40 px mark, not a page image
    <img
      src={src}
      width={size}
      height={size}
      alt=""
      aria-hidden
      loading="lazy"
      decoding="async"
      onError={() => setBroken(true)}
      className={className}
      style={{ flex: "none", width: size, height: size, objectFit: "cover", borderRadius: "50%" }}
    />
  );
}

export function SymbolIcon({
  symbol,
  size = 14,
  className = "",
}: {
  symbol: string;
  size?: number;
  className?: string;
}) {
  // A stock token takes its share's mark: SPYB / QQQB the index marks below, a share its disc.
  const raw = baseOf(symbol);
  const share = shareOf(raw);
  const base = share === "SPY" || share === "QQQ" ? share : raw;
  // One gradient id per INSTANCE: a fixed id shared by every Solana mark on a page is legal SVG
  // but resolves to whichever was painted first, and a hidden first instance blanks the rest.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 64 64",
    "aria-hidden": true,
    className,
    style: { flex: "none" as const },
  };

  /** A disc carrying a ticker: a share or a lettered coin in its own colour, any other coin last. */
  const tickerDisc = (text: string, bg: string, fg: string) => (
    <svg {...common}>
      <circle cx="32" cy="32" r="32" fill={bg} />
      <text
        x="32"
        y="33"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={text.length > 4 ? 14 : text.length > 3 ? 16 : 19}
        fontWeight="800"
        fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
        fill={fg}
      >
        {text}
      </text>
    </svg>
  );
  if (share && STOCKS[share]) return tickerDisc(share, STOCKS[share]!.bg, "#ffffff");
  const coin = LETTER_COINS[base];
  const logo = share ? null : logoOf(base);
  // a coin that had only its lettered disc now shows its logo, the disc stays the fallback
  if (coin && logo)
    return <CoinLogo src={logo} size={size} className={className} fallback={tickerDisc(base, coin.bg, coin.fg ?? "#ffffff")} />;
  if (coin) return tickerDisc(base, coin.bg, coin.fg ?? "#ffffff");

  if (base === FOREX_TILE)
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#0e5a8a" />
        <circle
          cx="32"
          cy="32"
          r="26.5"
          fill="none"
          stroke="#fff"
          strokeOpacity=".25"
          strokeWidth="1.5"
        />
        <text
          x="32"
          y="33"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="26"
          fontWeight="800"
          letterSpacing="-1"
          fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
          fill="#fff"
        >
          FX
        </text>
      </svg>
    );

  // A pair whose currency has no sign in `CURRENCY` falls through to the generic disc below
  // instead of dereferencing `undefined` — that crashed the lessons page's symbol list the day
  // GBPUSD was listed before GBP had a sign (2026-10-04).
  const fxA = CURRENCY[base.slice(0, 3)];
  const fxB = CURRENCY[base.slice(3, 6)];
  if (FOREX_PAIRS.has(base) && fxA && fxB) {
    const a = fxA;
    const b = fxB;
    const disc = (c: typeof a, cx: number, cy: number) => (
      <g>
        <circle cx={cx} cy={cy} r="20" fill={c.bg} stroke="#0b0d10" strokeOpacity=".55" strokeWidth="2" />
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={c.size}
          fontWeight="700"
          fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
          fill={c.fg}
        >
          {c.sign}
        </text>
      </g>
    );
    return (
      <svg {...common}>
        {disc(b, 42, 40)}
        {disc(a, 22, 24)}
      </svg>
    );
  }

  if (base === "BTC")
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#f7931a" />
        {/* the mark, at its own 14° tilt, from the bitcoin.org geometry */}
        <path
          fill="#fff"
          d="M46.11 27.44c.64-4.26-2.6-6.55-7.04-8.07l1.44-5.77-3.51-.88-1.4 5.62c-.92-.23-1.87-.45-2.81-.66l1.41-5.66-3.51-.88-1.44 5.77c-.77-.17-1.52-.35-2.25-.53v-.02l-4.84-1.21-.93 3.75s2.6.6 2.55.63c1.42.36 1.68 1.3 1.64 2.04l-1.64 6.57c.1.02.22.06.36.12l-.37-.09-2.3 9.21c-.17.43-.61 1.08-1.6.83.03.05-2.55-.64-2.55-.64l-1.74 4.02 4.57 1.14c.85.21 1.68.44 2.5.65l-1.46 5.83 3.51.88 1.44-5.78c.96.26 1.89.5 2.8.73l-1.44 5.75 3.51.88 1.46-5.82c5.99 1.13 10.49.68 12.38-4.74 1.53-4.36-.08-6.88-3.23-8.52 2.3-.53 4.03-2.04 4.49-5.16zM38.08 38.7c-1.09 4.36-8.43 2-10.81 1.41l1.93-7.73c2.38.6 10.02 1.77 8.88 6.32zm1.09-11.32c-.99 3.97-7.1 1.95-9.08 1.46l1.75-7.01c1.98.49 8.36 1.41 7.33 5.55z"
        />
      </svg>
    );

  if (base === "ETH")
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#627eea" />
        {/* the octahedron seen edge-on: upper half solid, lower half and far faces shaded */}
        <g fill="#fff">
          <path fillOpacity=".6" d="M33 8v17.74l15 6.7z" />
          <path d="M33 8 18 32.44l15-6.7z" />
          <path fillOpacity=".6" d="M33 43.94V56l15-20.76z" />
          <path d="M33 56V43.94L18 35.24z" />
          <path fillOpacity=".2" d="m33 41.15 15-8.71-15-6.69z" />
          <path fillOpacity=".6" d="m18 32.44 15 8.71V25.75z" />
        </g>
      </svg>
    );

  // USDC — Circle's mark: the dollar sign inside a broken ring, white on its blue.
  if (base === "USDC")
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#2775ca" />
        <path fill="#fff" d="M26.5 50.4c0 .9-.7 1.4-1.6 1.1C14.5 48.2 7 38.4 7 27.5S14.5 6.8 24.9 3.5c.9-.3 1.6.2 1.6 1.1v2.1c0 .6-.5 1.3-1.1 1.5-8.2 3-14.1 10.8-14.1 19.3s5.9 16.3 14.1 19.3c.6.2 1.1.9 1.1 1.5zM37.5 50.4v-2.1c0-.6.5-1.3 1.1-1.5 8.2-3 14.1-10.8 14.1-19.3S46.8 11.2 38.6 8.2c-.6-.2-1.1-.9-1.1-1.5V4.6c0-.9.7-1.4 1.6-1.1C49.5 6.8 57 16.6 57 27.5s-7.5 20.7-17.9 24c-.9.3-1.6-.2-1.6-1.1z" transform="translate(0 4.5)" />
        <path fill="#fff" d="M33.5 37.1c0-2.4-1.5-3.3-4.4-3.7-2.1-.3-2.5-.9-2.5-1.9s.7-1.7 2.2-1.7c1.3 0 2 .4 2.4 1.5.1.2.3.4.6.4h1.1c.4 0 .6-.3.6-.6-.2-1.7-1.4-2.9-3.1-3.3v-1.9c0-.4-.3-.7-.7-.7h-1.1c-.4 0-.7.3-.7.7v1.8c-2.1.3-3.5 1.7-3.5 3.5 0 2.3 1.4 3.2 4.3 3.6 2 .3 2.6.8 2.6 1.9s-1 1.9-2.4 1.9c-1.9 0-2.5-.8-2.7-1.9-.1-.3-.3-.5-.6-.5H24c-.4 0-.6.3-.6.6.2 1.8 1.4 3.1 3.6 3.5v1.9c0 .4.3.7.7.7h1.1c.4 0 .7-.3.7-.7v-1.9c2.2-.4 3.9-1.8 3.9-3.8z" />
      </svg>
    );

  // USDT — Tether's mark: the ₮ on its green.
  if (base === "USDT")
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#26a17b" />
        <path fill="#fff" d="M35.3 34.6v-.003c-.2.013-1.3.08-3.6.08-1.9 0-3.2-.056-3.7-.08v.003c-7.3-.32-12.7-1.59-12.7-3.1s5.4-2.78 12.7-3.1v4.94c.5.036 1.9.12 3.7.12 2.2 0 3.3-.1 3.6-.12v-4.94c7.3.32 12.7 1.59 12.7 3.1s-5.4 2.78-12.7 3.1m0-6.71v-4.42h10.2V16.7H18.5v6.77h10.2v4.42c-8.3.38-14.5 2.02-14.5 3.99s6.2 3.61 14.5 3.99v14.16h7.3V35.87c8.3-.38 14.5-2.02 14.5-3.99s-6.2-3.61-14.5-3.99" />
      </svg>
    );

  // POL — Polygon's mark: two interlocked hexagonal links forming the M, white on its purple.
  if (base === "POL" || base === "MATIC")
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#8247e5" />
        <path
          fill="#fff"
          d="M41.6 25.7c-.7-.4-1.6-.4-2.4 0l-5.5 3.3-3.7 2.1-5.5 3.3c-.7.4-1.6.4-2.4 0l-4.3-2.6c-.7-.4-1.2-1.2-1.2-2.1v-5c0-.8.4-1.6 1.2-2.1l4.3-2.5c.7-.4 1.6-.4 2.4 0l4.3 2.6c.7.4 1.2 1.2 1.2 2.1v3.3l3.7-2.2v-3.4c0-.8-.4-1.6-1.2-2.1l-7.9-4.7c-.7-.4-1.6-.4-2.4 0l-8.1 4.8c-.8.4-1.2 1.2-1.2 2v9.4c0 .8.4 1.6 1.2 2.1l8 4.7c.7.4 1.6.4 2.4 0l5.5-3.2 3.7-2.2 5.5-3.2c.7-.4 1.6-.4 2.4 0l4.3 2.5c.7.4 1.2 1.2 1.2 2.1v5c0 .8-.4 1.6-1.2 2.1l-4.2 2.5c-.7.4-1.6.4-2.4 0l-4.3-2.5c-.7-.4-1.2-1.2-1.2-2.1v-3.3l-3.7 2.2v3.4c0 .8.4 1.6 1.2 2.1l8 4.7c.7.4 1.6.4 2.4 0l8-4.7c.7-.4 1.2-1.2 1.2-2.1v-9.5c0-.8-.4-1.6-1.2-2.1z"
        />
      </svg>
    );

  if (base === "SOL")
    return (
      <svg {...common}>
        <defs>
          <linearGradient
            id={`sol-${uid}`}
            x1="8"
            y1="52"
            x2="56"
            y2="12"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#9945ff" />
            <stop offset=".5" stopColor="#8752f3" />
            <stop offset="1" stopColor="#14f195" />
          </linearGradient>
        </defs>
        <circle cx="32" cy="32" r="32" fill="#0b0b14" />
        {/* three slanted bars; the middle one runs the other way */}
        <g fill={`url(#sol-${uid})`}>
          <path d="M19.7 39.3a1.2 1.2 0 0 1 .84-.35h28.9c.53 0 .8.65.42 1.03l-5.6 5.63a1.2 1.2 0 0 1-.84.35H14.52c-.53 0-.8-.65-.42-1.03z" />
          <path d="M19.7 18.34a1.2 1.2 0 0 1 .84-.34h28.9c.53 0 .8.64.42 1.02l-5.6 5.64a1.2 1.2 0 0 1-.84.34H14.52c-.53 0-.8-.64-.42-1.02z" />
          <path d="M44.26 28.75a1.2 1.2 0 0 0-.84-.35H14.52c-.53 0-.8.65-.42 1.03l5.6 5.63a1.2 1.2 0 0 0 .84.35h28.9c.53 0 .8-.65.42-1.03z" />
        </g>
      </svg>
    );

  if (base === "XRP")
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#23292f" />
        <path
          fill="#fff"
          d="M45.6 15h5.3L39.9 26a11 11 0 0 1-15.6 0L13.3 15h5.3l8.4 8.4a7.2 7.2 0 0 0 10.2 0zM18.5 49h-5.3l11.1-11.1a11 11 0 0 1 15.6 0L51 49h-5.3l-8.5-8.5a7.2 7.2 0 0 0-10.2 0z"
        />
      </svg>
    );

  // BNB: its gold disc and diamond mark — one diamond in the centre and four smaller ones around
  // it, each a square rotated 45 degrees so the mark stays crisp at every icon size.
  if (base === "BNB")
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#f3ba2f" />
        <g fill="#fff">
          <path d="M32 24.5 39.5 32 32 39.5 24.5 32z" />
          <path d="M32 11.5 36.2 15.7 32 19.9 27.8 15.7z" />
          <path d="M32 44.1 36.2 48.3 32 52.5 27.8 48.3z" />
          <path d="M15.7 27.8 19.9 32 15.7 36.2 11.5 32z" />
          <path d="M48.3 27.8 52.5 32 48.3 36.2 44.1 32z" />
        </g>
      </svg>
    );

  // NEAR — NEAR Protocol's mark: the white N whose diagonal ends in two notched stems, on its
  // black disc.
  if (base === "NEAR")
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#000000" />
        <path
          fill="#ffffff"
          d="M40.6 16.5 35 24.8c-.4.6.4 1.3.9.8l5.5-4.8c.2-.2.5 0 .5.2v15.1c0 .3-.4.4-.6.2L24.5 16.9a2.9 2.9 0 0 0-2.2-1H21.7A2.9 2.9 0 0 0 18.8 18.8v26.4a2.9 2.9 0 0 0 5.4 1.6L29.8 38.5c.4-.6-.4-1.3-.9-.8l-5.5 4.8c-.2.2-.5 0-.5-.2V27.2c0-.3.4-.4.6-.2l16.8 20.1c.5.6 1.3 1 2.2 1h.6a2.9 2.9 0 0 0 2.9-2.9V18.8a2.9 2.9 0 0 0-5.4-2.3z"
        />
      </svg>
    );

  // GOLD — our label for the gold contracts (XAUUSDT today, XAUTUSDT before 2026-09-09, and
  // PAXGUSDT before that). `base` strips the USDT suffix, so XAUUSDT arrives here as XAU.
  if (base === "XAUT" || base === "XAU" || base === "XAUUSD" || base === "PAXG" || base === "GOLD")
    return (
      <svg {...common}>
        <defs>
          <radialGradient id={`au-${uid}`} cx="38%" cy="30%" r="75%">
            <stop offset="0" stopColor="#fbe7a1" />
            <stop offset=".55" stopColor="#e2b93b" />
            <stop offset="1" stopColor="#9c6f12" />
          </radialGradient>
        </defs>
        <circle cx="32" cy="32" r="32" fill={`url(#au-${uid})`} />
        <circle
          cx="32"
          cy="32"
          r="26.5"
          fill="none"
          stroke="#fff5cf"
          strokeOpacity=".55"
          strokeWidth="1.5"
        />
        {/* two stacked bars, the top one set back — the ingot silhouette, not a coin face */}
        <path d="M17 41h30l-4.2-9.6H21.2z" fill="#5c3f08" />
        <path d="M21.2 31.4h21.6L39.4 24H24.6z" fill="#7a5511" />
        <path d="M23 26.2h18l-1.1-2.6H24.1z" fill="#fff3c4" fillOpacity=".7" />
        <path
          d="M18.9 36.6h26.2l-.9-2.2H19.8z"
          fill="#fff3c4"
          fillOpacity=".5"
        />
      </svg>
    );

  // SILVER — XAGUSDT (read on Yahoo SI=F). Gold's ingot on a silver disc, so the two metals read
  // as a pair and are told apart by colour alone at 14px.
  if (base === "XAG" || base === "XAGUSD" || base === "SILVER")
    return (
      <svg {...common}>
        <defs>
          <radialGradient id={`ag-${uid}`} cx="38%" cy="30%" r="75%">
            <stop offset="0" stopColor="#f5f7fa" />
            <stop offset=".55" stopColor="#bfc6cf" />
            <stop offset="1" stopColor="#6b7480" />
          </radialGradient>
        </defs>
        <circle cx="32" cy="32" r="32" fill={`url(#ag-${uid})`} />
        <circle cx="32" cy="32" r="26.5" fill="none" stroke="#ffffff" strokeOpacity=".6" strokeWidth="1.5" />
        <path d="M17 41h30l-4.2-9.6H21.2z" fill="#3e454e" />
        <path d="M21.2 31.4h21.6L39.4 24H24.6z" fill="#565f6a" />
        <path d="M23 26.2h18l-1.1-2.6H24.1z" fill="#ffffff" fillOpacity=".7" />
        <path d="M18.9 36.6h26.2l-.9-2.2H19.8z" fill="#ffffff" fillOpacity=".5" />
      </svg>
    );

  // EQUITY INDICES — the Nasdaq 100 (QQQUSDT, cTrader US TECH 100) and the S&P 500 (SPYUSDT, cTrader
  // US 500). Each has its own colour AND mark, so the two are told apart at 14px where the ticker
  // letters are unreadable: Nasdaq a blue disc with a rising line, the S&P a red disc with three bars.
  if (base === "QQQ" || base === "US TECH 100")
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#0b6bcb" />
        <path d="M13 42 25 31l8 6 17-17" fill="none" stroke="#ffffff" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
        <text x="32" y="56" textAnchor="middle" fontSize="11" fontWeight="700" fontFamily="system-ui, -apple-system, Segoe UI, sans-serif" fill="#ffffff">
          NDX
        </text>
      </svg>
    );
  if (base === "SPY" || base === "US 500")
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#c8102e" />
        <g fill="#ffffff">
          <rect x="17" y="30" width="7" height="14" rx="1.5" />
          <rect x="28.5" y="22" width="7" height="22" rx="1.5" />
          <rect x="40" y="15" width="7" height="29" rx="1.5" />
        </g>
        <text x="32" y="56" textAnchor="middle" fontSize="11" fontWeight="700" fontFamily="system-ui, -apple-system, Segoe UI, sans-serif" fill="#ffffff">
          SPX
        </text>
      </svg>
    );

  // DOW 30 — US30, cTrader only. Its own colour and mark beside the other two indices and gold: a
  // purple disc with a white stepped line, so at 14px it never reads as the Nasdaq's blue line, the
  // S&P's red bars, or gold's yellow coin.
  if (base === "US30" || base === "US 30")
    return (
      <svg {...common}>
        <circle cx="32" cy="32" r="32" fill="#6b3fa0" />
        <path d="M12 42h9v-8h9v-8h9v-8h13" fill="none" stroke="#ffffff" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
        <text x="32" y="56" textAnchor="middle" fontSize="11" fontWeight="700" fontFamily="system-ui, -apple-system, Segoe UI, sans-serif" fill="#ffffff">
          DJI
        </text>
      </svg>
    );

  // OIL — our label for CLUSDT (WTI crude, read on Yahoo CL=F; owner 2026-09-18: "the name keep
  // OIL add icon like we did for gold"). A crude drop on a dark disc, with the highlight a drop
  // actually has, so it never reads as GOLD's disc at 14px.
  if (base === "CL" || base === "OIL" || base === "WTI" || base === "XTIUSD")
    return (
      <svg {...common}>
        <defs>
          <radialGradient id={`oil-${uid}`} cx="40%" cy="35%" r="70%">
            <stop offset="0" stopColor="#3a3632" />
            <stop offset="1" stopColor="#141210" />
          </radialGradient>
        </defs>
        <circle cx="32" cy="32" r="32" fill={`url(#oil-${uid})`} />
        <circle
          cx="32"
          cy="32"
          r="26.5"
          fill="none"
          stroke="#fff"
          strokeOpacity=".12"
          strokeWidth="1.5"
        />
        <path
          d="M32 13c0 0-12.5 15.6-12.5 24.2a12.5 12.5 0 0 0 25 0C44.5 28.6 32 13 32 13z"
          fill="#0a0908"
          stroke="#8f887c"
          strokeOpacity=".7"
          strokeWidth="1.4"
        />
        <path
          d="M25.4 37.8a6.6 6.6 0 0 0 4.4 6.3"
          stroke="#fff"
          strokeOpacity=".8"
          strokeWidth="2.2"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    );

  // ANY OTHER COIN (a pair the admin just added gets a mark at once): its ticker on a disc whose
  // colour is derived from the ticker, so it is the same on every page — never an empty space.
  // EVERY OTHER COIN: its logo when the console has one (`COIN_LOGOS`), else the ticker disc below
  if (logo) {
    const h0 = [...base].reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) % 360, 0);
    return (
      <CoinLogo src={logo} size={size} className={className}
        fallback={tickerDisc(base.slice(0, 5), `hsl(${h0} 55% 42%)`, "#ffffff")} />
    );
  }
  if (/^[A-Z0-9]{2,10}$/.test(base)) {
    let h = 0;
    for (const ch of base) h = (h * 31 + ch.charCodeAt(0)) % 360;
    return tickerDisc(base.slice(0, 5), `hsl(${h} 55% 42%)`, "#ffffff");
  }
  return null;
}

/** Icon + display label as one unit, so the two never drift apart at a call site.
 *  The LABEL is not re-derived here — it comes from `symbolLabel` in lib/api, which stays the one
 *  place that decides XAUUSDT renders as GOLD. lib/api imports nothing from components, so this
 *  direction has no cycle. */
export function SymbolTag({
  symbol,
  size = 14,
  className = "",
  named = false,
}: {
  symbol: string | null | undefined;
  size?: number;
  className?: string;
  /** Show the market's name (Solana, Apple) with the ticker small beside it — the Spot pages name
   *  a pair by its market. A coin with no known name shows its ticker alone. */
  named?: boolean;
}) {
  const sym = symbol ?? "";
  const label = symbolLabel(sym);
  const name = named ? symbolName(sym) : label;
  return (
    <span
      className={`inline-flex items-center gap-1.5 align-middle ${className}`}
    >
      <SymbolIcon symbol={sym} size={size} />
      {named ? <PairInfo symbol={sym}>{name}</PairInfo> : <span>{name}</span>}
      {name !== label && <span className="text-[10px] text-faint">{label}</span>}
    </span>
  );
}

/** Descriptions already read on this page: a profile, `null` for "none found". A refusal by the
 *  source is not kept, so the next hover asks again. */
const profiles = new Map<string, AssetProfile | null>();
const profileReads = new Map<string, Promise<AssetProfile | null>>();
function readProfile(symbol: string): Promise<AssetProfile | null> {
  const known = profiles.get(symbol);
  if (known !== undefined) return Promise.resolve(known);
  let p = profileReads.get(symbol);
  if (!p) {
    p = getAssetProfile(symbol)
      .then((v) => v, (e) => {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      })
      .then((v) => {
        profiles.set(symbol, v);
        return v;
      })
      .finally(() => profileReads.delete(symbol));
    profileReads.set(symbol, p);
  }
  return p;
}

/** A pair's name that shows the market's short description while the pointer rests on it (or it
 *  has keyboard focus): what the project or company does, its category, a link to its site.
 *  `symbol` is a pair (SOLUSDT, AAPLXUSDT) or a share's ticker (AAPL). The card is fixed to the
 *  viewport so a table or a scrolling list cannot clip it; it flips above the name near the
 *  bottom of the screen. Read on the first hover only, never on page load. */
export function PairInfo({ symbol, children }: { symbol: string; children: ReactNode }) {
  const anchor = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [at, setAt] = useState<{ x: number; y: number; above: boolean } | null>(null);
  const [state, setState] = useState<"reading" | "none" | "unavailable" | AssetProfile>("reading");
  const sym = symbol.toUpperCase();
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  const show = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const r = anchor.current?.getBoundingClientRect();
      if (!r) return;
      const above = r.bottom + 220 > window.innerHeight && r.top > 240;
      setAt({ x: Math.max(8, Math.min(r.left, window.innerWidth - 8 - 304)), y: above ? r.top - 6 : r.bottom + 6, above });
      const known = profiles.get(sym);
      setState(known === undefined ? "reading" : known ?? "none");
      readProfile(sym).then(
        (v) => setState(v ?? "none"),
        () => setState("unavailable"),
      );
    }, 250);
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setAt(null), 150);
  };
  if (!sym) return <span>{children}</span>;
  return (
    <span
      ref={anchor}
      tabIndex={0}
      className="cursor-help rounded-sm underline decoration-dotted decoration-faint underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {at && (
        <span
          role="tooltip"
          onMouseEnter={() => timer.current && clearTimeout(timer.current)}
          onMouseLeave={hide}
          style={{ left: at.x, top: at.y, transform: at.above ? "translateY(-100%)" : undefined }}
          className="fixed z-50 block w-[19rem] cursor-auto whitespace-normal rounded-md border border-border bg-bg-2 p-3 text-left text-xs font-normal normal-case leading-relaxed text-muted no-underline shadow-lg"
        >
          {typeof state === "object" ? (
            <>
              <span className="flex items-baseline justify-between gap-2">
                <span className="font-semibold text-primary">{state.name}</span>
                <span className="font-mono text-[10px] text-faint">
                  {state.ticker}
                  {state.rank ? ` · #${state.rank}` : ""}
                </span>
              </span>
              {state.tags.length > 0 && (
                <span className="mt-1 flex flex-wrap gap-1">
                  {state.tags.map((t) => (
                    <span key={t} className="rounded border border-border/60 px-1.5 py-px text-[10px] text-faint">{t}</span>
                  ))}
                </span>
              )}
              <span className="mt-1.5 block text-primary/90">
                {state.description || `${state.source} has no description for ${state.name}.`}
              </span>
              <span className="mt-1.5 flex items-center justify-between gap-2 text-[10px] text-faint">
                <span>Source: {state.source}</span>
                {state.url && /^https?:\/\//.test(state.url) && (
                  <a href={state.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                    Website
                  </a>
                )}
              </span>
            </>
          ) : state === "reading" ? (
            <span>Reading the description…</span>
          ) : state === "none" ? (
            <span>No description found for {sym}.</span>
          ) : (
            <span>The description can’t be read right now. Hover again in a minute.</span>
          )}
        </span>
      )}
    </span>
  );
}

/** A venue's small mark: binance's diamond, bybit's, MEXC's and cTrader's initials on their colours. Drawn
 *  inline (no image files); used on the Connect button and wherever an account is picked. */
export function VenueMark({ venue, size = 16 }: { venue: string | null | undefined; size?: number }) {
  const v = (venue ?? "").toLowerCase();
  if (v === "binance")
    return (
      <svg viewBox="0 0 16 16" width={size} height={size} role="img" className="shrink-0">
        <title>binance</title>
        <rect width="16" height="16" rx="4" fill="#181A20" />
        <g fill="#F0B90B" transform="translate(8 8) rotate(45)">
          <rect x="-1.4" y="-1.4" width="2.8" height="2.8" />
          <rect x="-4.6" y="-1.1" width="2.2" height="2.2" />
          <rect x="2.4" y="-1.1" width="2.2" height="2.2" />
          <rect x="-1.1" y="-4.6" width="2.2" height="2.2" />
          <rect x="-1.1" y="2.4" width="2.2" height="2.2" />
        </g>
      </svg>
    );
  if (v === "bybit")
    return (
      <svg viewBox="0 0 16 16" width={size} height={size} role="img" className="shrink-0">
        <title>bybit</title>
        <rect width="16" height="16" rx="4" fill="#17181E" />
        <text x="8" y="11.6" textAnchor="middle" fontSize="10" fontWeight="800" fontFamily="Arial, sans-serif" fill="#F7A600">B</text>
      </svg>
    );
  if (v === "mexc")
    return (
      <svg viewBox="0 0 16 16" width={size} height={size} role="img" className="shrink-0">
        <title>MEXC</title>
        <rect width="16" height="16" rx="4" fill="#0B0E17" />
        <text x="8" y="11.6" textAnchor="middle" fontSize="10" fontWeight="800" fontFamily="Arial, sans-serif" fill="#2E6CF6">M</text>
      </svg>
    );
  if (v === "ctrader")
    return (
      <svg viewBox="0 0 16 16" width={size} height={size} role="img" className="shrink-0">
        <title>cTrader</title>
        <rect width="16" height="16" rx="4" fill="#E8392C" />
        <text x="8" y="11.8" textAnchor="middle" fontSize="11" fontWeight="800" fontFamily="Arial, sans-serif" fill="#FFFFFF">c</text>
      </svg>
    );
  return null;
}

/** The exchange's name as it writes it. */
export const VENUE_NAME: Record<string, string> = { bybit: "Bybit", binance: "Binance", mexc: "MEXC", ctrader: "cTrader" };

/** THE SPOT ACCOUNT PICKER, the same on Spot, Stock, VCA and Portfolio: each account as a card with
 *  its exchange's mark and name and the account's own label, the chosen one in the accent (yellow)
 *  like every other choice on these pages — the cards a user without a subscription is shown. */
/** THE SPOT ACCOUNT LAST CHOSEN, kept in this browser so /spot, /spot/stock, /spot/vca and
 *  /spot/portfolio open on the same account (each fell back to the first one). A convenience only:
 *  storage can be unavailable, and then the first account is used, as before. */
const SPOT_ACCOUNT_KEY = "twoke.spot.account";
export function rememberedSpotAccount(accounts: { accountId: string }[]): string {
  let id = "";
  try {
    id = window.localStorage.getItem(SPOT_ACCOUNT_KEY) ?? "";
  } catch {
    id = "";
  }
  return accounts.some((a) => a.accountId === id) ? id : (accounts[0]?.accountId ?? "");
}
export function rememberSpotAccount(id: string): void {
  try {
    if (id) window.localStorage.setItem(SPOT_ACCOUNT_KEY, id);
  } catch {
    /* storage unavailable: nothing to remember */
  }
}

export function SpotAccountCards({
  accounts,
  accountId,
  onPick,
}: {
  accounts: Array<{ accountId: string; label: string; venue: string | null; isDemo?: boolean }>;
  accountId: string | null;
  onPick: (id: string) => void;
}) {
  return (
    // Each card keeps room for the exchange's name and the account's label: as many 10rem
    // columns as fit, so a lone account is not squeezed and three accounts sit side by side.
    <div className="grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-2" role="radiogroup" aria-label="Account">
      {accounts.map((a) => {
        const on = a.accountId === accountId;
        const v = (a.venue ?? "").toLowerCase();
        return (
          <button
            key={a.accountId}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onPick(a.accountId)}
            className={`flex min-w-0 items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${on ? "border-accent bg-accent/10" : "border-border/60 hover:border-accent"}`}
          >
            <VenueMark venue={a.venue} size={32} />
            <span className="min-w-0">
              <span className={`block text-sm font-semibold ${on ? "text-accent" : "text-primary"}`}>
                {VENUE_NAME[v] ?? a.venue}
                {a.isDemo ? <span className="font-normal text-muted"> demo</span> : null}
              </span>
              <span className="block break-words text-[11px] text-muted">{a.label}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
